import { useEffect, useState } from 'react';
import { MonitorSmartphone, Search } from 'lucide-react';
import { Input } from '@leanopx/design-system';
import { Button } from '@leanopx/design-system';
// MobileSplash mounts OUTSIDE the RouterProvider (so narrow viewports
// see a single full-screen message that works even if the router errors
// out), which means `@tanstack/react-router`'s <Link> would throw on
// mount. Plain anchors are fine — the router re-hydrates on click.

/** "Optimized for desktop" splash below 768px with an entity-search
 *  fallback. `/m/*` routes bypass — they're built for narrow viewports. */
export function MobileSplash() {
  const [narrow, setNarrow] = useState<boolean>(() =>
    typeof window === 'undefined' ? false : window.innerWidth < 768,
  );
  const [path, setPath] = useState<string>(() =>
    typeof window === 'undefined' ? '/' : window.location.pathname,
  );
  const [query, setQuery] = useState('');

  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    // Poll location.pathname instead of monkey-patching history.pushState
    // — TanStack Router wraps history internally; a second wrapper on top
    // creates restore-order hazards on unmount (and conflicts with any
    // other consumer doing the same). 200ms is imperceptible for a
    // viewport splash and adds no measurable CPU.
    let lastPath = window.location.pathname;
    const onPath = () => {
      const current = window.location.pathname;
      if (current !== lastPath) {
        lastPath = current;
        setPath(current);
      }
    };
    window.addEventListener('popstate', onPath);
    const poll = window.setInterval(onPath, 200);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('popstate', onPath);
      window.clearInterval(poll);
    };
  }, []);

  const isMobileCompanion = path === '/m' || path.startsWith('/m/');
  if (!narrow || isMobileCompanion) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex flex-col items-center justify-center gap-6 bg-[var(--color-canvas)] p-6 text-center text-[var(--color-ink)]"
      role="dialog"
      aria-modal="true"
    >
      <MonitorSmartphone className="size-10 text-[var(--color-accent)]" aria-hidden />
      <div className="space-y-2">
        <h1 className="font-serif text-2xl tracking-tight">{'Optimized for desktop'}</h1>
        <p className="mx-auto max-w-sm text-sm text-[var(--color-ink-muted)]">
          {
            'The full LeanOpx workspace is designed for an analyst workstation. Revisit on a screen ≥ 768px.'
          }
        </p>
      </div>

      <form
        className="flex w-full max-w-sm gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          const q = query.trim();
          if (!q) return;
          window.location.href = `/entity/${encodeURIComponent(q)}`;
        }}
      >
        <div className="relative flex-1">
          <Search
            className="pointer-events-none absolute start-2 top-1/2 size-4 -translate-y-1/2 text-[var(--color-ink-muted)]"
            aria-hidden
          />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={'Search entity'}
            className="ps-8"
          />
        </div>
        <Button type="submit" variant="accent" size="sm">
          {'Open'}
        </Button>
      </form>

      <a
        href="/"
        className="text-xxs uppercase tracking-wider text-[var(--color-ink-muted)] underline-offset-2 hover:text-[var(--color-ink)] hover:underline"
      >
        {'Continue anyway'}
      </a>
    </div>
  );
}
